// Per-drone morph: `position` holds the start slot, aTarget the destination.
// Each drone eases start->target on its own staggered clock so the swarm
// "breathes" into formation instead of snapping as one rigid block.
attribute vec3 aTarget;
attribute vec3 aColorA;
attribute vec3 aColorB;
attribute float aSeed;

uniform float uMorph;      // 0..1 global morph progress
uniform float uTime;
uniform float uSize;
uniform float uBrightness;
uniform float uStagger;    // 0..1 spread of per-drone start times
uniform int uTransKind;    // transition effect id (0 = plain morph); see sequencer.ts
uniform float uTransAmt;   // transition effect strength
uniform float uAudioLevel; // 0..1 overall audio energy (0 when not reacting)
uniform float uAudioBeat;  // 0..1 kick spike (0 when not reacting)
uniform float uShimmer;    // treble sparkle amount (0 = calm twinkle)
uniform float uHueShift;   // accumulated hue-drift angle (radians, 0 = off)
uniform float uCymAmp;     // cymatics standing-wave amplitude (0 = off)
uniform float uCymM;       // plate mode numbers + combination sign
uniform float uCymN;
uniform float uCymMix;
uniform float uCymL;       // plate side length (scene units)
uniform float uCymSpeed;   // oscillation rate (rad/s)
uniform float uScale;      // Size: scales the shape/swarm space (1 = as authored)

varying vec3 vColor;
varying float vTwinkle;

const float PI = 3.14159265;

float smoother(float x) {
  return x * x * x * (x * (x * 6.0 - 15.0) + 10.0);
}

// rotate a colour about the grey axis (Rodrigues) — cheap hue drift
vec3 hueRotate(vec3 c, float a) {
  const vec3 k = vec3(0.57735026919);
  float ca = cos(a);
  return c * ca + cross(k, c) * sin(a) + k * dot(k, c) * (1.0 - ca);
}

void main() {
  float span = max(1.0 - uStagger, 1e-4);
  float local = clamp((uMorph - aSeed * uStagger) / span, 0.0, 1.0);
  float e = smoother(local);

  vec3 pos = mix(position, aTarget, e);

  // transition presets — each drone "does a thing" at its own mid-morph
  // (mid peaks at 0.5, so the effect blooms then resolves back onto the target)
  float mid = sin(local * PI);
  float fade = 1.0;
  if (uTransKind == 1) {                 // explode: burst outward then reform
    pos += normalize(pos + vec3(1e-4)) * (mid * uTransAmt);
  } else if (uTransKind == 2) {          // implode: collapse through the centre
    pos *= (1.0 - 0.9 * mid);
  } else if (uTransKind == 3) {          // spiral: swirl about the Y axis
    float a = mid * uTransAmt;
    float ca = cos(a), sa = sin(a);
    pos = vec3(ca * pos.x + sa * pos.z, pos.y, -sa * pos.x + ca * pos.z);
  } else if (uTransKind == 4) {          // fade: dim out through the middle
    fade = 1.0 - 0.92 * mid;
  } else if (uTransKind == 5) {          // fountain: arc up and fall in
    pos.y += mid * uTransAmt;
  }

  // cymatics: the plate oscillates as a true standing wave — antinodes pump,
  // nodal lines hold still (sand-form drones sit AT the zeros, so they rest)
  if (uCymAmp > 0.0) {
    vec2 uv = pos.xz / uCymL;
    float plate = cos(uCymM * PI * uv.x) * cos(uCymN * PI * uv.y)
                + uCymMix * cos(uCymN * PI * uv.x) * cos(uCymM * PI * uv.y);
    pos.y += uCymAmp * 0.5 * plate * sin(uTime * uCymSpeed);
  }

  // small idle drift so the swarm is alive even when holding a shape
  float d = 0.025;
  pos.x += sin(uTime * 0.6 + aSeed * 30.0) * d;
  pos.y += cos(uTime * 0.5 + aSeed * 22.0) * d;
  pos.z += sin(uTime * 0.7 + aSeed * 17.0) * d;

  vec3 col = mix(aColorA, aColorB, e);

  pos *= uScale;

  vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
  gl_Position = projectionMatrix * mvPosition;

  // perspective-correct point size, sized in px at ~8 units out.
  // Audio makes each point swell on the beat and with overall energy.
  float audioSize = 1.0 + uAudioBeat * 0.55 + uAudioLevel * 0.22;
  gl_PointSize = clamp(uSize * audioSize * (8.0 / max(-mvPosition.z, 0.001)), 1.0, 48.0);

  // treble deepens + speeds the twinkle (Shimmer effect)
  vTwinkle = 0.82 + (0.18 + 0.55 * uShimmer) * sin(uTime * (3.0 + uShimmer * 10.0) + aSeed * 120.0);
  vec3 outCol = col * uBrightness * fade * (1.0 + uAudioLevel * 0.6 + uAudioBeat * 0.5);
  if (uHueShift != 0.0) outCol = hueRotate(outCol, uHueShift);
  vColor = outCol;
}
